import { ReactNode, useEffect, useState } from "react";
import { ArrowLeftRight, BarChart3, Bell, ChevronDown, ClipboardList, Factory, History, Home, LayoutDashboard, Lock, LogOut, Menu, Package, PanelLeftClose, PanelLeftOpen, RotateCcw, ScanLine, Send, ShieldCheck, SlidersHorizontal, Upload, Warehouse, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import type { AdminUser } from "../admin/api";
import { AdminBreadcrumb } from "../admin/components/AdminBreadcrumb";
import { defaultLogoUrl } from "@/utils/branding";

type ErpLayoutProps = {
  children: ReactNode;
  title: string;
  user: AdminUser | null;
  onLogout: () => void;
};

type ErpNavItem = {
  key: string;
  label: string;
  href: string;
  icon: ReactNode;
};

type ErpNavGroup = {
  key: string;
  label: string;
  icon: ReactNode;
  items: ErpNavItem[];
};

const sidebarStorageKey = "fulling_erp_sidebar_collapsed";

const navGroups: ErpNavGroup[] = [
  {
    key: "inventory",
    label: "庫存管理",
    icon: <Package size={18} />,
    items: [
      { key: "erp-inventory", label: "庫存總覽", href: "/erp/inventory", icon: <Package size={18} /> },
      { key: "erp-stock-units", label: "庫存序號明細", href: "/erp/stock-units", icon: <ScanLine size={18} /> },
      { key: "erp-stock-movements", label: "庫存異動紀錄", href: "/erp/stock-movements", icon: <History size={18} /> },
    ],
  },
  {
    key: "inventory-counts",
    label: "盤點管理",
    icon: <ClipboardList size={18} />,
    items: [
      { key: "erp-inventory-counts", label: "盤點單列表", href: "/erp/inventory-counts", icon: <ClipboardList size={18} /> },
      { key: "erp-inventory-counts", label: "建立盤點單", href: "/erp/inventory-counts/new", icon: <ClipboardList size={18} /> },
      { key: "erp-stock-adjustments", label: "調整紀錄列表", href: "/erp/stock-adjustments", icon: <SlidersHorizontal size={18} /> },
      { key: "erp-stock-adjustments", label: "建立庫存調整", href: "/erp/stock-adjustments/new", icon: <SlidersHorizontal size={18} /> },
    ],
  },
  {
    key: "stock-transfers",
    label: "調撥管理",
    icon: <ArrowLeftRight size={18} />,
    items: [
      { key: "erp-stock-transfers", label: "調撥單列表", href: "/erp/stock-transfers", icon: <ArrowLeftRight size={18} /> },
      { key: "erp-stock-transfers", label: "建立調撥單", href: "/erp/stock-transfers/new", icon: <ArrowLeftRight size={18} /> },
    ],
  },
  {
    key: "purchase",
    label: "採購管理",
    icon: <ClipboardList size={18} />,
    items: [
      { key: "erp-purchase-orders", label: "採購單列表", href: "/erp/purchase-orders", icon: <ClipboardList size={18} /> },
      { key: "erp-purchase-orders", label: "採購需求 / 建議", href: "/erp/purchase-suggestions", icon: <ClipboardList size={18} /> },
      { key: "erp-purchase-orders", label: "新增採購單", href: "/erp/purchase-orders/new", icon: <ClipboardList size={18} /> },
      { key: "erp-purchase-receipts", label: "採購入庫驗收", href: "/erp/purchase-receipts", icon: <ShieldCheck size={18} /> },
      { key: "erp-rma-orders", label: "退貨換貨 / RMA", href: "/erp/rma-orders", icon: <RotateCcw size={18} /> },
      { key: "erp-payables", label: "供應商請款付款", href: "/erp/payables", icon: <ClipboardList size={18} /> },
    ],
  },
  {
    key: "outbound",
    label: "出庫作業",
    icon: <Send size={18} />,
    items: [
      { key: "erp-outbound-orders", label: "出庫單列表", href: "/erp/outbound-orders", icon: <Send size={18} /> },
      { key: "erp-outbound-orders", label: "建立出庫單", href: "/erp/outbound-orders/new", icon: <Send size={18} /> },
      { key: "erp-quality-inspections", label: "工程檢查 / QC", href: "/erp/quality-inspections", icon: <ShieldCheck size={18} /> },
    ],
  },
  {
    key: "reports",
    label: "管理報表",
    icon: <BarChart3 size={18} />,
    items: [
      { key: "erp-reports", label: "ERP 報表", href: "/erp/reports", icon: <BarChart3 size={18} /> },
      { key: "erp-tasks", label: "ERP 通知待辦", href: "/erp/tasks", icon: <Bell size={18} /> },
      { key: "erp-import-batches", label: "匯入批次管理", href: "/erp/import-batches", icon: <Upload size={18} /> },
      { key: "erp-audit-logs", label: "操作稽核紀錄", href: "/erp/audit-logs", icon: <History size={18} /> },
      { key: "erp-attachments", label: "附件中心", href: "/erp/attachments", icon: <ClipboardList size={18} /> },
    ],
  },
  {
    key: "settings",
    label: "基礎設定",
    icon: <Warehouse size={18} />,
    items: [
      { key: "erp-suppliers", label: "供應商列表", href: "/erp/suppliers", icon: <Factory size={18} /> },
      { key: "erp-suppliers", label: "新增供應商", href: "/erp/suppliers/new", icon: <Factory size={18} /> },
      { key: "erp-warehouses", label: "倉庫管理", href: "/erp/warehouses", icon: <Warehouse size={18} /> },
      { key: "erp-accounting-periods", label: "會計期間鎖定", href: "/erp/accounting-periods", icon: <Lock size={18} /> },
    ],
  },
];

const rootNavItems: ErpNavItem[] = [
  { key: "erp-dashboard", label: "ERP 工作台", href: "/erp/dashboard", icon: <LayoutDashboard size={18} /> },
];

function BrandLogo({ collapsed }: { collapsed: boolean }) {
  if (collapsed) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-white p-0">
        <img className="h-full w-full object-contain" src={defaultLogoUrl} alt="富聆聽健康" />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
      <div className="flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-md p-0">
        <img className="h-full w-full object-contain" src={defaultLogoUrl} alt="富聆聽健康" />
      </div>
      <div className="min-w-0">
        <div className="shrink-0 whitespace-nowrap text-[22px] font-extrabold leading-none tracking-normal text-[#315746]">
          富聆聽<span className="text-[#d4a574]">健康</span>
        </div>
        <div className="mt-1 whitespace-nowrap text-[11px] font-bold tracking-[.08em] text-[#7b877f]">ERP PORTAL</div>
      </div>
    </div>
  );
}

function hasPermission(user: AdminUser | null, key: string) {
  if (!user) return false;
  if (user.IsSuperAdmin) return true;
  return (user.Permissions || []).includes(key);
}

function NavLink({ collapsed, indented, item, onNavigate }: { collapsed?: boolean; indented?: boolean; item: ErpNavItem & { active?: boolean }; onNavigate?: () => void }) {
  return (
    <a
      className={`flex min-h-11 items-center rounded-md px-3 py-2 text-sm font-bold transition ${collapsed ? "justify-center" : ""} ${indented ? "ml-4" : ""} ${
        item.active ? "bg-[#e9f4ec] text-[#245438]" : "text-[#59665f] hover:bg-[#f0f5f2] hover:text-[#315746]"
      }`}
      href={item.href}
      onClick={onNavigate}
      title={item.label}
    >
      <span className="shrink-0">{item.icon}</span>
      {!collapsed ? <span className="ml-2 min-w-0 truncate">{item.label}</span> : null}
    </a>
  );
}

function ErpMenuGroup({
  collapsed,
  expanded,
  group,
  onNavigate,
  onToggle,
}: {
  collapsed: boolean;
  expanded: boolean;
  group: ErpNavGroup & { active?: boolean };
  onNavigate?: () => void;
  onToggle: () => void;
}) {
  const activeClass = group.active ? "bg-[#e9f4ec] text-[#245438]" : "text-[#59665f] hover:bg-[#f0f5f2] hover:text-[#315746]";

  if (collapsed) {
    return (
      <div className="group relative">
        <button aria-label={group.label} className={`flex h-12 w-full items-center justify-center rounded-md transition ${activeClass}`} type="button">
          {group.icon}
        </button>
        <div className="absolute left-[44px] top-0 z-[90] hidden h-12 w-5 group-hover:block group-focus-within:block" />
        <div className="pointer-events-none fixed bottom-3 left-[70px] top-3 z-[100] flex w-[286px] -translate-x-1 opacity-0 transition group-hover:pointer-events-auto group-hover:translate-x-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-x-0 group-focus-within:opacity-100">
          <div className="flex min-h-0 w-full flex-col overflow-hidden rounded-md border border-[#dfe6e1] bg-white shadow-[0_16px_42px_rgba(32,61,50,.14)]">
            <div className="flex items-center gap-2 border-b border-[#edf0ee] px-4 py-3 text-sm font-bold text-[#315746]">
              {group.icon}
              {group.label}
            </div>
            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-3">
              {group.items.map((item) => (
                <NavLink item={item} key={`${item.key}-${item.href}`} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button
        aria-expanded={expanded}
        className={`flex h-10 w-full items-center justify-between rounded-md px-3 text-sm font-bold transition ${activeClass}`}
        onClick={onToggle}
        type="button"
      >
        <span className="inline-flex min-w-0 items-center gap-2">
          <span className="shrink-0">{group.icon}</span>
          <span className="truncate">{group.label}</span>
        </span>
        <ChevronDown className={`shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} size={16} />
      </button>
      {expanded ? (
        <div className="space-y-1">
          {group.items.map((item) => (
            <NavLink indented item={item} key={`${item.key}-${item.href}`} onNavigate={onNavigate} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function ErpLayout({ children, title, user, onLogout }: ErpLayoutProps) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(sidebarStorageKey) === "1");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);
  const sidebarCollapsed = collapsed && !mobileMenuOpen;
  const desktopContentPadding = collapsed ? "lg:pl-[76px]" : "lg:pl-[320px]";
  const currentPath = location.pathname;

  function isActive(path: string) {
    if (path.endsWith("/new")) return currentPath === path;
    if (currentPath.startsWith(`${path}/`) && currentPath.endsWith("/new")) return false;
    return currentPath === path || currentPath.startsWith(`${path}/`);
  }

  const visibleRootItems = rootNavItems.filter((item) => hasPermission(user, item.key)).map((item) => ({ ...item, active: isActive(item.href) }));
  const visibleGroups = navGroups
    .map((group) => {
      const items = group.items.filter((item) => hasPermission(user, item.key)).map((item) => ({ ...item, active: isActive(item.href) }));
      return { ...group, items, active: items.some((item) => item.active) };
    })
    .filter((group) => group.items.length > 0);
  const activeGroupKey = visibleGroups.find((group) => group.active)?.key ?? null;

  useEffect(() => {
    localStorage.setItem(sidebarStorageKey, collapsed ? "1" : "0");
  }, [collapsed]);

  function setCollapsedAndStore(value: boolean) {
    setCollapsed(value);
  }

  useEffect(() => {
    setExpandedGroupKey(activeGroupKey);
  }, [activeGroupKey, currentPath]);

  return (
    <main className="portal-rwd erp-rwd min-h-screen bg-[#f6f7f5] text-[#24352d]">
      {mobileMenuOpen ? (
        <button aria-label="關閉 ERP 選單" className="fixed inset-0 z-30 bg-[#10251c]/35 lg:hidden" onClick={() => setMobileMenuOpen(false)} type="button" />
      ) : null}

      <aside className={`fixed inset-y-0 left-0 z-40 w-[300px] border-r border-[#dfe6e1] bg-white transition-[transform,width] duration-200 lg:translate-x-0 ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"} ${collapsed ? "lg:w-[76px]" : "lg:w-[320px]"}`}>
        <div className="flex h-full flex-col">
          <div className={`flex h-[86px] items-center border-b border-[#edf0ee] px-4 ${sidebarCollapsed ? "justify-center" : "justify-between gap-2"}`}>
            <BrandLogo collapsed={sidebarCollapsed} />
            <button aria-label="關閉 ERP 選單" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2] lg:hidden" onClick={() => setMobileMenuOpen(false)} type="button">
              <X size={18} />
            </button>
            {!sidebarCollapsed ? (
              <button aria-label="收合 ERP 選單" className="hidden h-9 w-9 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2] lg:inline-flex" onClick={() => setCollapsed(true)} type="button">
                <PanelLeftClose size={18} />
              </button>
            ) : null}
          </div>

          <nav className={`flex-1 space-y-2 px-3 py-4 ${sidebarCollapsed ? "overflow-visible" : "overflow-y-auto"}`}>
            {visibleRootItems.map((item) => (
              <NavLink collapsed={sidebarCollapsed} item={item} key={`${item.key}-${item.href}`} onNavigate={() => setMobileMenuOpen(false)} />
            ))}
            {visibleGroups.map((group) => (
              <ErpMenuGroup
                collapsed={sidebarCollapsed}
                expanded={expandedGroupKey === group.key}
                group={group}
                key={group.key}
                onNavigate={() => setMobileMenuOpen(false)}
                onToggle={() => setExpandedGroupKey((current) => (current === group.key ? null : group.key))}
              />
            ))}
          </nav>

          <div className="border-t border-[#edf0ee] p-3">
            <button className={`inline-flex h-10 items-center rounded-md border border-[#cfd8d2] bg-white font-bold text-[#315746] transition hover:bg-[#f0f5f2] ${sidebarCollapsed ? "w-full justify-center px-0" : "w-full justify-center gap-2 px-3"}`} onClick={onLogout} title="登出 ERP" type="button">
              <LogOut size={18} />
              {!sidebarCollapsed ? "登出 ERP" : null}
            </button>
          </div>
        </div>
      </aside>

      <div className={`min-h-screen min-w-0 transition-[padding] duration-200 ${desktopContentPadding}`}>
        <header className="sticky top-0 z-20 border-b border-[#dfe6e1] bg-white/95 backdrop-blur">
          <div className="flex min-h-[56px] items-center justify-between gap-4 px-4 py-2.5 md:min-h-[64px] md:px-6 md:py-3">
            <div className="flex min-w-0 items-center gap-3">
              <button
                aria-label={collapsed ? "展開 ERP 選單" : "收合 ERP 選單"}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] bg-white text-[#315746] transition hover:bg-[#f0f5f2]"
                onClick={() => {
                  if (window.matchMedia("(max-width: 1023px)").matches) {
                    setMobileMenuOpen(true);
                    return;
                  }
                  setCollapsedAndStore(!collapsed);
                }}
                type="button"
              >
                {collapsed ? <PanelLeftOpen size={18} /> : <Menu size={18} />}
              </button>
              <AdminBreadcrumb
                currentTag="h1"
                items={[
                  { label: "ERP 後台", href: "/erp/dashboard", icon: <Home size={13} /> },
                  { label: "ERP 管理" },
                  { label: title },
                ]}
              />
            </div>
            <div className="hidden shrink-0 items-center gap-2 text-sm text-[#59665f] sm:flex">
              <ShieldCheck size={18} />
              <span>{user?.DisplayName || "ERP 使用者"}</span>
            </div>
          </div>
        </header>

        <section className="w-full max-w-none px-4 py-4 md:px-6 md:py-5">{children}</section>
      </div>
    </main>
  );
}
